/**
 * projectJob.js — canonical Phase 5 projection bundle for a single job.
 *
 * Phase 5.5 R3 audit fix (BLOCKER #1 — three projection systems coexist):
 *
 * Callers used to wire the velocity→throughput→eta→cost pipeline inline,
 * with subtle variance between SPLDashboard (Phase 5), AdminProjects
 * (computeSmartProjections), ProjectDashboard (computeSmartProjections),
 * BossDashboard (none), and ClientPortal (SQL RPC). Same job ⇒ four
 * different ETAs. This is the one function every internal-facing UI
 * should call so every dashboard shows the same numbers.
 *
 * Pure function. No DB, no React, no Date.now side effects (caller
 * injects `now`). Safe to call from any layer.
 *
 * Returned bundle:
 *   {
 *     // Inputs (for debug / tooltips)
 *     annotationsDone, annotationsTarget, itemsRemaining, weeksOfHistory,
 *     velocities, deadline, budget,
 *
 *     // Phase 5 core projections (all Projection-shape, all banded)
 *     throughput, eta, cost,
 *
 *     // Primary-surface metrics (tier + date + signed delta)
 *     deadlineHit,   // { probability, tier, projectedDate, deadlineDate, daysDelta, overdue, reason? }
 *     costOverrun,   // { probability, tier, projectedCost, budget, dollarsDelta, reason? }
 *
 *     // Convenience (legacy-shape for migrating consumers off
 *     // computeSmartProjections; may be removed once all consumers are
 *     // migrated)
 *     estimatedWeeksLeft: eta.point,
 *     teamWeeklyOutput: throughput.point,
 *   }
 */

import { computeAnnotatorVelocities } from '../computeProjections';
import { projectThroughput } from './throughputProjection';
import { projectETA, projectedCompletionDate } from './etaProjection';
import { projectCost } from './costProjection';
import { projectDeadlineHit, projectCostOverrun } from './deadlineProjection';
import {
  resolveAnnotationsDone,
  resolveItemsRemaining,
  resolveWeeksOfHistory,
  resolveAssignmentRate,
} from '../projectMetrics';
import { WORKING_ASSIGNMENT_STATUSES } from '../../constants/enums';

// Default hours-per-week for an annotator when job.hours_per_week_max is null.
// 20 = the historical default for part-time annotators on the platform.
// Phase 11 slot 1 (Invariant #16).
const DEFAULT_HOURS_PER_WEEK = 20;
// Minimum complexity factor — same floor as etaProjection. A weight < 0.1 is
// data-entry error.
const MIN_COMPLEXITY_FACTOR = 0.1;

/**
 * Canonical assignment filter for projection inputs.
 *
 * R3 audit H1 fix — the "same job ⇒ same numbers everywhere" invariant
 * was FALSE before this change because each dashboard pre-filtered
 * differently:
 *   - SPL / Boss: timeframe-filtered + WORKING only
 *   - AdminProjects: WORKING only, no timeframe filter
 *   - ProjectDashboard: displayAssignments (lifetime, all statuses
 *     including Completed/Offboarded)
 *
 * The velocity + throughput computation should only consider annotators
 * WORKING on the job RIGHT NOW. Completed/Offboarded assignments must
 * not contribute velocity (they're not producing items anymore).
 * Timeframe filters are a UI concept (which range of data to DISPLAY);
 * they do not scope who's currently working.
 *
 * projectJob now filters internally so all callers get identical output
 * for the same (job, full-assignment-list) pair. Callers MAY still
 * pre-filter if they want (idempotent — WORKING ∩ WORKING = WORKING),
 * but MUST NOT pass timeframe-filtered or status-expanded sets.
 */
function filterActiveAssignments(assignments) {
  if (!Array.isArray(assignments)) return [];
  return assignments.filter((a) => a && WORKING_ASSIGNMENT_STATUSES.includes(a.status));
}

/**
 * Build the full Phase 5 projection bundle for one job.
 *
 * @param {object} args
 * @param {object} args.job — job row with annotations_target, start_date, end_date, sla_delivery_date, budget, hours_per_week_max
 * @param {Array} args.assignments — assignments for this job (filter to working/onboarding upstream if needed)
 * @param {Array} args.qualityLogs — quality_log entries (job-scoped OR org-scoped; filter happens here)
 * @param {Record<string, number>} args.batchSummary — batch-system completion counts keyed by job.id
 * @param {object} args.config — PROJECTION_* constants
 * @param {Date|number} [args.now] — inject for test stability / React identity
 * @returns {object} bundle as documented in module header
 */
export function projectJob({
  job,
  assignments,
  // qualityLogs reserved for future quality-aware projections (e.g. when
  // complexity factor ships in Phase 5.5 Step 5). Currently unused because
  // qualityFactor = 1.0 is the correct value (see H1 audit fix — rework
  // is handled by separate rework batches, not multiplier inflation).
  qualityLogs: _qualityLogs,
  batchSummary,
  config,
  now = new Date(),
}) {
  // R3 H1 audit fix: filter to WORKING annotators ourselves so every
  // caller gets the same canonical input. Accepts pre-filtered sets too
  // (idempotent).
  const activeAssignments = filterActiveAssignments(assignments);

  // Resolve inputs (canonical helpers — single source of truth).
  const annotationsTarget = Number(job?.annotations_target) || 0;
  const annotationsDone = resolveAnnotationsDone(job?.id, batchSummary, activeAssignments);
  const itemsRemaining = resolveItemsRemaining(annotationsTarget, annotationsDone);
  // Phase 2 cross-phase sweep fix: thread `now` through the velocity
  // computation so every wall-clock-dependent value inside one `projectJob`
  // call reads the same instant. Without this, `computeAnnotatorVelocities`
  // read a fresh `Date.now()` internally — at day boundaries `weeksActive`
  // bucketed against a slightly later wall-clock than the rest of the
  // projection pipeline. Same invariant class as R13 MED-2 / R15 MED-1.
  const velocities = computeAnnotatorVelocities(activeAssignments, job?.hours_per_week_max || DEFAULT_HOURS_PER_WEEK, now);
  const weeksOfHistory = resolveWeeksOfHistory(velocities, job?.start_date, now);

  // Deadline resolution — end_date preferred, fall back to SLA delivery date.
  const deadlineRaw = job?.end_date || job?.sla_delivery_date;
  const deadline = deadlineRaw ? new Date(deadlineRaw) : null;

  // Budget: job.budget if set; no fallback heuristics (a nil budget is a
  // legitimate "no budget set" state, not a projection failure).
  const budget = Number.isFinite(Number(job?.budget)) && Number(job?.budget) > 0
    ? Number(job.budget)
    : null;

  // R3 H2 audit fix: compute dollarsSpent = sum of (hours × rate) across
  // active assignments. The cost-overrun probability needs this so the
  // comparison is `spent + remaining_cost vs budget`, not
  // `remaining_cost vs budget` (which would report "well under budget"
  // even when the total was clearly over).
  // R4 LOW-5 + R6 MED-1 audit fix: route through resolveAssignmentRate
  // so every caller across the codebase uses the SAME rate-resolution
  // semantics. The resolver uses `??` (nullish coalescing) so legit
  // rate=0 isn't silently replaced by confirmed_rate.
  const dollarsSpent = activeAssignments.reduce((sum, a) => {
    const hours = Number(a?.hours_logged) || 0;
    return sum + hours * resolveAssignmentRate(a);
  }, 0);

  // ─── Phase 5 core pipeline ───
  // Phase 5.5 Step 5: complexity_weight is a per-item difficulty
  // multiplier sourced from `job.complexity_weight` (migration 237).
  // A weight of 2.0 means "each item takes 2× baseline velocity to
  // complete." Null/missing/out-of-bound defaults to 1.0 (no
  // adjustment) to preserve projections for pre-Step-5 jobs.
  //
  // This is NOT the old qualityFactor (100/accuracy) which R3 H1
  // removed — that was rework inflation and double-counted because
  // rework batches are tracked separately. Complexity is intrinsic
  // item difficulty, orthogonal to rework.
  // R5 LOW-1 audit fix: expose the CLAMPED complexity factor so callers
  // tooltipping proj.complexityFactor see the value actually used in
  // the ETA math, not the unclamped input. projectETA clamps to
  // [0.1, config.PROJECTION_QUALITY_FACTOR_MAX] internally — mirror
  // that here for a single source of truth.
  const rawComplexity = Number(job?.complexity_weight);
  const rawComplexityValid = Number.isFinite(rawComplexity) && rawComplexity > 0 ? rawComplexity : 1.0;
  const complexityCap = config?.PROJECTION_QUALITY_FACTOR_MAX ?? 10;
  const complexityFactor = Math.min(complexityCap, Math.max(MIN_COMPLEXITY_FACTOR, rawComplexityValid));
  const complexityFactorRaw = rawComplexityValid;  // unclamped input, for debug / admin surfaces
  const throughput = projectThroughput(velocities, weeksOfHistory, config, now);
  const eta = projectETA(itemsRemaining, throughput, complexityFactor, config, now);
  const cost = projectCost(eta, velocities, config, now);

  // ─── Primary-surface derived metrics ───
  const deadlineHit = projectDeadlineHit(eta, deadline, config, now);
  const costOverrun = projectCostOverrun(cost, budget, dollarsSpent, config);

  // ─── Optional: explicit projected-completion-date helper (same source
  //     as deadlineHit.projectedDate; exposed separately for convenience
  //     when no deadline exists) ───
  const { pointDate, lowDate, highDate } = projectedCompletionDate(eta, now);

  return {
    // Inputs (debug / tooltip)
    annotationsDone,
    annotationsTarget,
    itemsRemaining,
    weeksOfHistory,
    velocities,
    deadline,
    budget,
    dollarsSpent,
    complexityFactor,     // Phase 5.5 Step 5 — CLAMPED (used in math)
    complexityFactorRaw,  // R5 LOW-1: unclamped input for admin surfaces

    // Phase 5 core
    throughput,
    eta,
    cost,

    // Primary-surface derived
    deadlineHit,
    costOverrun,

    // Projected completion date range (derived from eta)
    projectedDate: pointDate,
    projectedDateRange: { low: lowDate, high: highDate },

    // Legacy shape for migrating consumers off computeSmartProjections
    estimatedWeeksLeft: eta.point,
    teamWeeklyOutput: throughput.point,
  };
}

/**
 * Portfolio-level rollup — counts per deadline tier across a list of
 * projected jobs. Used by BossDashboard to answer "how many projects
 * are at risk right now."
 *
 * @param {Array<ReturnType<projectJob>>} projectedJobs
 * @returns {{
 *   byDeadlineTier: Record<string, number>,
 *   byCostTier: Record<string, number>,
 *   overdueCount: number,
 *   atRiskCount: number,  // very_low + low combined
 *   total: number
 * }}
 */
export function rollupPortfolio(projectedJobs) {
  const byDeadlineTier = {
    very_high: 0, high: 0, fifty_fifty: 0, low: 0, very_low: 0, insufficient_data: 0,
  };
  const byCostTier = {
    very_high: 0, high: 0, fifty_fifty: 0, low: 0, very_low: 0, insufficient_data: 0,
  };
  let overdueCount = 0;
  const list = Array.isArray(projectedJobs) ? projectedJobs : [];
  for (const p of list) {
    const dTier = p?.deadlineHit?.tier || 'insufficient_data';
    const cTier = p?.costOverrun?.tier || 'insufficient_data';
    byDeadlineTier[dTier] = (byDeadlineTier[dTier] || 0) + 1;
    byCostTier[cTier] = (byCostTier[cTier] || 0) + 1;
    if (p?.deadlineHit?.overdue) overdueCount += 1;
  }
  const atRiskCount = byDeadlineTier.very_low + byDeadlineTier.low;
  return {
    byDeadlineTier,
    byCostTier,
    overdueCount,
    atRiskCount,
    total: list.length,
  };
}
